import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';
import { setAuthData, getAuthData, clearAuthData } from '../lib/utils';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { token, user: storedUser } = getAuthData();
    if (token && storedUser) {
      setUser(storedUser);
      // V19.9 — Reconcile the stored user record against /api/auth/me,
      // but tolerate transient failures. Only a true 401 (token rejected
      // by the backend) clears auth — every other failure mode (network
      // blip, edge-proxy CORS hiccup, backend rolling restart) keeps the
      // user signed in with the existing stored record. The SSO handoff
      // now persists a JWT-derived stub user before any API call, so
      // the user is never wedged offline waiting on this reconcile.
      authAPI.getMe()
        .then(res => setUser(res.data))
        .catch(err => {
          if (err?.response?.status === 401) {
            clearAuthData();
            setUser(null);
          }
          // else: keep stored user — transient failure, not auth error.
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login(email, password);
    if (res.data.mfa_required) {
      return { mfaRequired: true, email, debugCode: res.data.mfa_code_debug };
    }
    setAuthData(res.data.token, res.data.user);
    setUser(res.data.user);
    return { success: true, user: res.data.user };
  };

  const verifyMFA = async (email, code) => {
    const res = await authAPI.verifyMFA(email, code);
    setAuthData(res.data.token, res.data.user);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = () => {
    clearAuthData();
    // Drop the bare 'token' key too (set by /sso handoff).
    try { localStorage.removeItem('token'); } catch (e) { /* ignore */ }
    setUser(null);
    // In production SSO mode, hand the user back to the portal so they
    // can sign out fully or re-launch any other tile from there.
    const portalUrl = process.env.REACT_APP_PORTAL_URL;
    if (portalUrl) {
      window.location.href = portalUrl;
    }
  };

  const value = {
    user,
    loading,
    login,
    verifyMFA,
    logout,
    isStaff: user?.role === 'staff' || user?.role === 'admin',
    isClient: user?.role === 'client',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
